import React, { useState } from 'react';
import {
  DatePicker,
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvCard,
  SalvCheckbox,
  SalvModal,
  SalvRadio,
  SalvRadioGroup,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTagsInput,
} from '@salvapp/ui-components';
import { faCheck, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch } from 'react-redux';
import {
  InternalApiEntityType,
  InternalApiScreeningFlowDraft,
  InternalApiScreeningFlowTest,
  InternalApiScreeningFlowTestEntitySelectionModeEnum,
} from '../../../api-internal';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../../common/errors/helpers';
import { useCreateScreeningTestForDraft } from './useCreateScreeningTestForDraft';
import { useQueryClearanceRules } from './useQueryClearanceRules';

const SELECTION_DATE_RANGE = 'DATE_RANGE';
const SELECTION_ENTITY_IDS = 'ENTITY_IDS';

const MAX_ENTITY_IDS = 1000;

enum Behaviour {
  MatchLive = 'MATCH_LIVE',
  Customise = 'CUSTOMISE',
}

enum RulesMode {
  AllLive = 'ALL_LIVE',
  Pick = 'PICK',
}

const GOODLISTING_KEYS_HELP_URL = 'https://help.salv.com/en/articles/236437-screening-flows#h_76cec361ea';

interface Props {
  draft: InternalApiScreeningFlowDraft;
  onClose: () => void;
}

const splitIds = (text: string): string[] =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');

const isPerson = (entityType: InternalApiEntityType): boolean => entityType === InternalApiEntityType.PERSON;

const entityTypeLabel = (entityType: InternalApiEntityType): string =>
  isPerson(entityType) ? 'Person' : 'Transaction';

const goodlistingDescription = (entityType: InternalApiEntityType): React.ReactNode => {
  if (isPerson(entityType)) {
    return 'Only affects persons screened before — reuses past goodlisted alerts for the same person and list.';
  }
  return (
    <>
      {'Only affects transactions matching past goodlisting decisions (based on '}
      <a href={GOODLISTING_KEYS_HELP_URL} target='_blank' rel='noreferrer'>
        goodlisting keys
      </a>
      ).
    </>
  );
};

const screenedEntitiesLabel = (entityType: InternalApiEntityType): string =>
  isPerson(entityType) ? 'Use only automatically re-screened entities' : 'Use only screened transactions';

const screenedEntitiesSubtext = (entityType: InternalApiEntityType): string | undefined =>
  isPerson(entityType) ? 'Excludes persons in segments excluded from automatic re-screening.' : undefined;

export const AddDraftTestModal = ({ draft, onClose }: Props) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = useCreateScreeningTestForDraft();

  const [selectionMode, setSelectionMode] = useState<string>(SELECTION_DATE_RANGE);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [entityIds, setEntityIds] = useState<string[]>([]);
  const [useOnlyScreenedEntities, setUseOnlyScreenedEntities] = useState(false);

  const [behaviour, setBehaviour] = useState<Behaviour>(Behaviour.MatchLive);
  const [rulesMode, setRulesMode] = useState<RulesMode>(RulesMode.AllLive);
  const [selectedHandles, setSelectedHandles] = useState<string[]>([]);
  const [applyGoodlisting, setApplyGoodlisting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: clearanceRulesData } = useQueryClearanceRules(draft.entityType!);
  const clearanceRules = (clearanceRulesData ?? []).filter((rule) => rule.enabled === true);

  const byDateRange = selectionMode === SELECTION_DATE_RANGE;
  const matchLive = behaviour === Behaviour.MatchLive;

  const isRulesPickEmpty =
    behaviour === Behaviour.Customise && rulesMode === RulesMode.Pick && selectedHandles.length === 0;

  const cleanedEntityIds = entityIds.map((id) => id.trim()).filter((id) => id !== '');
  const tooManyEntityIds = !byDateRange && cleanedEntityIds.length > MAX_ENTITY_IDS;

  const isSubmitDisabled =
    (byDateRange ? fromDate == null || toDate == null : cleanedEntityIds.length === 0) ||
    tooManyEntityIds ||
    isRulesPickEmpty;

  const handleSelectionModeChange = (value: string) => {
    setSelectionMode(value);
    if (value === SELECTION_ENTITY_IDS) {
      setUseOnlyScreenedEntities(false);
    }
  };

  const toggleHandle = (handle: string, checked: boolean) => {
    if (checked) {
      setSelectedHandles((prev) => [...prev, handle]);
    } else {
      setSelectedHandles((prev) => prev.filter((h) => h !== handle));
    }
  };

  const handleSubmit = () => {
    setErrorMessage(null);
    const allLiveRules = rulesMode === RulesMode.AllLive;

    const body: InternalApiScreeningFlowTest = {
      entitySelection: byDateRange
        ? {
            mode: InternalApiScreeningFlowTestEntitySelectionModeEnum.DATE_RANGE,
            fromTime: fromDate ? fromDate.startOf('day').toDate() : undefined,
            toTime: toDate ? toDate.endOf('day').toDate() : undefined,
          }
        : {
            mode: InternalApiScreeningFlowTestEntitySelectionModeEnum.ENTITY_IDS,
            entityIds: cleanedEntityIds,
          },
      useOnlyScreenedEntities: byDateRange ? useOnlyScreenedEntities : false,
      goodlistEnabled: matchLive ? true : applyGoodlisting,
      clearanceRuleHandles: matchLive || allLiveRules ? undefined : selectedHandles,
    };

    mutate(
      { draftId: draft.id!, body },
      {
        onSuccess: () => {
          dispatch(showFeedback({ message: 'Test scheduled successfully', success: true }));
          onClose();
        },
        onError: async (error) => {
          const message = await parseErrorMessage(error);
          setErrorMessage(message);
        },
      }
    );
  };

  return (
    <div data-testid='add-draft-test-modal'>
      <SalvModal show={true} onHide={isPending ? () => {} : onClose}>
        <SalvModal.Header closeButton={!isPending}>
          <div className='title-m'>Add new test</div>
          <div className='body-s text-secondary'>{entityTypeLabel(draft.entityType!)} screening flow</div>
        </SalvModal.Header>
        <SalvModal.Body>
          {errorMessage && (
            <SalvBanner
              type={SalvBannerType.DANGER}
              accent={SalvBannerAccent.LIGHT}
              message={errorMessage}
              className='m-b-16'
            />
          )}
          <div className='title-s m-b-4'>
            <span className='text-secondary m-r-8'>1</span>What to test
          </div>
          <div className='text-secondary m-b-16'>
            Pick the records to run through this flow. Alerts are simulated — nothing reaches production.
          </div>

          <SalvCard className='m-b-24'>
            <SalvCard.Body>
              <SalvRadioToggleGroup value={selectionMode} onChange={handleSelectionModeChange}>
                <SalvRadioToggleButton label='By date range' value={SELECTION_DATE_RANGE} />
                <SalvRadioToggleButton label='By id' value={SELECTION_ENTITY_IDS} />
              </SalvRadioToggleGroup>

              {byDateRange && (
                <div className='m-t-16' style={{ display: 'flex', gap: '16px', position: 'relative', zIndex: 10 }}>
                  <div>
                    <div className='label-s m-b-4'>From created</div>
                    <DatePicker
                      value={fromDate}
                      onChange={(val: Dayjs | null) => setFromDate(val)}
                      isClearable
                      size='small'
                      showDefaultOptions={false}
                      max={dayjs()}
                    />
                  </div>
                  <div>
                    <div className='label-s m-b-4'>To created (inclusive)</div>
                    <DatePicker
                      value={toDate}
                      onChange={(val: Dayjs | null) => setToDate(val)}
                      isClearable
                      size='small'
                      showDefaultOptions={false}
                      max={dayjs()}
                    />
                  </div>
                </div>
              )}

              {!byDateRange && (
                <div className='m-t-16'>
                  <SalvTagsInput
                    label='Entity IDs'
                    value={entityIds}
                    onChange={(values) => setEntityIds(values)}
                    onPaste={(pastedText) => splitIds(pastedText)}
                    placeholder='Enter entity ID'
                  />
                  {tooManyEntityIds && (
                    <div className='body-s m-t-4' style={{ color: 'var(--danger-default)' }}>
                      {`A maximum of ${MAX_ENTITY_IDS} ids is allowed per test.`}
                    </div>
                  )}
                </div>
              )}

              {byDateRange && (
                <div className='m-t-16'>
                  <SalvCheckbox
                    label={screenedEntitiesLabel(draft.entityType!)}
                    selected={useOnlyScreenedEntities}
                    onChange={(checked) => setUseOnlyScreenedEntities(checked)}
                  />
                  {screenedEntitiesSubtext(draft.entityType!) && (
                    <div className='text-secondary small m-t-4'>{screenedEntitiesSubtext(draft.entityType!)}</div>
                  )}
                </div>
              )}
            </SalvCard.Body>
          </SalvCard>

          <div className='title-s m-b-4'>
            <span className='text-secondary m-r-8'>2</span>Screening behaviour
          </div>
          <div className='text-secondary m-b-16'>How closely the run should mirror production.</div>

          <SalvCard className='m-b-16'>
            <SalvCard.Body>
              <SalvRadioGroup
                ariaLabel='Screening behaviour'
                value={behaviour}
                onChange={(value) => setBehaviour(value as Behaviour)}
              >
                <SalvRadio
                  value={Behaviour.MatchLive}
                  label='Match my live screening'
                  description='Runs exactly as production would today.'
                />
                {matchLive && (
                  <div className='m-t-8 m-b-8' style={{ paddingLeft: '28px' }}>
                    <div className='m-b-8'>
                      <FontAwesomeIcon icon={faCheck} className='m-r-8' style={{ color: 'var(--success-default)' }} />
                      <strong>Clearance rules</strong>
                      <span className='text-secondary'> — all live versions applied.</span>
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faCheck} className='m-r-8' style={{ color: 'var(--success-default)' }} />
                      <strong>Goodlisting</strong>
                      <span className='text-secondary'> — {goodlistingDescription(draft.entityType!)}</span>
                    </div>
                  </div>
                )}
                <SalvRadio
                  value={Behaviour.Customise}
                  label='Customise'
                  description='Change clearance rules or goodlisting for this test only.'
                />
              </SalvRadioGroup>

              {!matchLive && (
                <div className='m-t-16'>
                  <div className='label-s m-b-8'>Clearance rules</div>
                  <SalvRadioToggleGroup value={rulesMode} onChange={(value) => setRulesMode(value as RulesMode)}>
                    <SalvRadioToggleButton label='All live (current)' value={RulesMode.AllLive} />
                    <SalvRadioToggleButton label='Pick rules' value={RulesMode.Pick} />
                  </SalvRadioToggleGroup>

                  {rulesMode === RulesMode.Pick && (
                    <div className='m-t-16'>
                      {clearanceRules.map((rule) => (
                        <div key={rule.handle ?? rule.reason} className='m-b-8'>
                          <SalvCheckbox
                            label={rule.reason}
                            selected={rule.handle ? selectedHandles.includes(rule.handle) : false}
                            onChange={(checked) => rule.handle && toggleHandle(rule.handle, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className='label-s m-t-24 m-b-8'>Goodlisting</div>
                  <SalvCheckbox
                    label='Apply goodlisting'
                    selected={applyGoodlisting}
                    onChange={(checked) => setApplyGoodlisting(checked)}
                  />
                  <div className='text-secondary small m-t-4'>{goodlistingDescription(draft.entityType!)}</div>
                </div>
              )}
            </SalvCard.Body>
          </SalvCard>
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton label='Close' variant='muted' onClick={onClose} disabled={isPending} />
          <SalvButton
            label={isPending ? 'Starting test…' : 'Run test'}
            variant='primary'
            icon={faPlay}
            onClick={handleSubmit}
            disabled={isPending || isSubmitDisabled}
          />
        </SalvModal.Footer>
      </SalvModal>
    </div>
  );
};

export default AddDraftTestModal;
